import React, {useEffect, useRef, useState} from 'react';
import {Checkbox, Col, DatePicker, Form, Input, message, Row} from 'antd';
import {ProForm, ProFormInstance} from '@ant-design/pro-form';
import {FieldProps, WdiCardTitle, WdiInput, WdiModalAppResult} from '@/components/WdiControls';
import {FormPageProps} from '@/components/WdiBus/FormPage';
import {ApplyFooter, ApplyFooterType} from '@/components/WdiBus/ApplyFooter';
import moment from 'moment';
import {FileUploadUtility, WdiFileUpload} from '@/components/WdiControls/WdiFileUpload';
import {Enum} from '@/enums/system';
import {ResponseStatus} from '@/utils/request';
import {initApplyPageContext, ViewEntityProps} from '../../components/data';
import DataStore from '@/components/GridWidget/DataStore';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {FormSaDetailsForApplyPanel} from './Details';
import SAWarningInfoApp from '../../components/WarningInformation';
import {FormSa, FormSaDetail} from '@/services/sales/sa';
import {WarningInfoAppProps} from '@/components/WdiBus/PanelForWarning';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {BOHQ} from '@/services/mdm/bohq';
import {BO} from '@/services/mdm/bo';
import {CustType} from '@/services/mdm/custtype';
import {CustSys} from '@/services/mdm/custsys';
import {SoldToReal} from '@/services/mdm/soldtoreal';
import {ShipTo} from '@/services/mdm/shipto';
import {DataOptions} from '@/services/basic/metadata';
import SoldToRealSelectApp from '@/pages/sales/sa/PriceDiscountApply/components/SoldToRealSelect';
import ShipToSelect from '@/pages/sales/sa/PriceDiscountApply/components/ShipToSelect';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {FormApi} from "@/services/sales";
import {RevenueType} from "@/services/mdm/revenuetype";

const {RangePicker} = DatePicker;

const getPageResult = (
  values: any,
  viewEntity: ViewEntityProps,
  statusId: number,
  dataSource: any[],
) => {
  const result: WdiModalAppResult = {code: ResponseStatus.UNKNOWN};

  const {
    applyScopeId, boIds, custTypeId, custSysCode, soldToRealCodes, shipToCodes, soldToRealCodesExclude,
    activityDate, supplyDate, fileInfo, ...entity
  } = values;

  const req: { form: any; formSa: any; formSaDetails: any[] } = {
    form: {
      statusId: statusId,
      pageTypeId: viewEntity.pageTypeId,
      formTypeId: viewEntity.formTypeId,
      budgetProjectId: viewEntity?.budgetProject?.budgetProjectId,
      userId: viewEntity?.userId,
      positionId: viewEntity?.positionId,
    },
    formSa: {},
    formSaDetails: [],
  };

  if (viewEntity.formId) {
    //草稿打开
    req.form.formId = viewEntity.formId;
  } else if (viewEntity.relatedFormId) {
    if (
      viewEntity.relationType === Enum.FormRelationType.REJECTED ||
      viewEntity.relationType === Enum.FormRelationType.MODIFIED
    ) {
      // 退回待修改,修改单据
      req.form.relatedFormId = viewEntity.relatedFormId;
      req.form.relationType = viewEntity.relationType;
    }
    req.form.formId = 0;
  }

  if (activityDate && activityDate.length == 2) {
    entity.activityStartDate = moment(activityDate[0]).format('YYYY-MM-DD');
    entity.activityEndDate = moment(activityDate[1]).format('YYYY-MM-DD');
  } else {
    result.message = '无效的活动期间';
    return result;
  }
  if (supplyDate && supplyDate.length == 2) {
    entity.supplyStartDate = moment(supplyDate[0]).format('YYYY-MM-DD');
    entity.supplyEndDate = moment(supplyDate[1]).format('YYYY-MM-DD');
  } else {
    result.message = '无效的交货期间';
    return result;
  }

  const info = FileUploadUtility.serialize(fileInfo);
  entity.fileName = info?.names;
  entity.filePath = info?.urls;

  entity.applyScopeId = WdiSelectFormat.getvalue(applyScopeId);
  entity.boIds = WdiSelectFormat.getvalue(boIds);
  entity.custTypeId = WdiSelectFormat.getvalue(custTypeId);
  entity.custSysCode = WdiSelectFormat.getvalue(custSysCode);
  entity.soldToRealCodes = soldToRealCodes?.codes;
  entity.shipToCodes = shipToCodes?.codes;
  entity.soldToRealCodesExclude = soldToRealCodesExclude?.codes;
  entity.revenueType = viewEntity.revenueType;
  req.formSa = entity;
  if (dataSource && dataSource.length) {
    dataSource.forEach((item, itemIndex) => {
      item.rowNumber = itemIndex + 1;
    });
  } else {
    result.message = '请填写费用明细!';
    return result;
  }

  req.formSaDetails = dataSource;
  result.data = req;
  result.code = ResponseStatus.SUCCESS;
  return result;
};

type PrevField = {
  applyScopeId?: number;
  boIds?: string;
  custTypeId?: number;
  custSysCode?: string;
  soldToRealCodes?: string;
  shipToCodes?: string;
  soldToRealCodesExclude?: string;
};

const PriceDiscountApplyApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();

  const [entity, setEntity] = useState<ViewEntityProps>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [warningSettings, setWarningSettings] = useState<WarningInfoAppProps>({visible: false});
  const [formSaDate, setFormSaDate] = useState<{
    activityStartDate?: any;
    activityEndDate?: any;
    supplyStartDate?: any;
    supplyEndDate?: any;
  }>();
  const [prevField, setPrevField] = useState<PrevField>({});
  const [editorRef, setEditorRef] = useState<any>();
  const [custSysRequired, setCustSysRequired] = useState<boolean>(false);

  const onDetailsChange = (data: any) => {
    setDataSource(data);
  };

  const handleApplyScopeChange = (applyScopeId: any) => {
    formRef.current?.resetFields([
      'boIds',
      'custTypeId',
      'soldToRealCodes',
      'shipToCodes',
      'custSysCode',
      'soldToRealCodesExclude',
    ]);
    handleFieldChange({
      applyScopeId: applyScopeId,
      boIds: undefined,
      custTypeId: undefined,
      soldToRealCodes: undefined,
      shipToCodes: undefined,
      custSysCode: undefined,
      soldToRealCodesExclude: undefined,
    });
  };

  const handleBoIdsChange = (boIds: any) => {
    formRef.current?.resetFields(['custTypeId', 'custSysCode', 'soldToRealCodes', 'shipToCodes', 'soldToRealCodesExclude',]);
    handleFieldChange({
      boIds: WdiSelectFormat.getvalue(boIds),
      custTypeId: undefined,
      custSysCode: undefined,
      soldToRealCodes: undefined,
      shipToCodes: undefined,
      soldToRealCodesExclude: undefined,
    });
  };

  const handleFieldChange = (field?: PrevField) => {
    setPrevField(Object.assign({}, prevField, field));
  };

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApplyPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;
        if (viewEntity.formSa) {
          formRef.current?.setFieldsValue({
            activityName: viewEntity.formSa?.activityName,
            activityDate: [
              moment(viewEntity.formSa?.activityStartDate),
              moment(viewEntity.formSa?.activityEndDate),
            ],
            supplyDate: [
              moment(viewEntity.formSa?.supplyStartDate),
              moment(viewEntity.formSa?.supplyEndDate),
            ],
            bohqId: viewEntity.formSa?.bohqId,
            applyScopeId: viewEntity.formSa.applyScopeId,
            custTypeId: viewEntity.formSa.custTypeId,
            custSysCode: viewEntity.formSa.custSysCode,
            isOld: viewEntity.formSa?.isOld,
            remark: viewEntity.formSa?.remark,
            fileInfo: FileUploadUtility.parse(
              viewEntity.formSa?.fileName,
              viewEntity.formSa?.filePath,
            ),
          });
          setFormSaDate({
            activityStartDate: viewEntity.formSa.activityStartDate,
            activityEndDate: viewEntity.formSa.activityEndDate,
            supplyStartDate: viewEntity.formSa.supplyStartDate,
            supplyEndDate: viewEntity.formSa.supplyEndDate,
          });
          handleFieldChange({
            applyScopeId: viewEntity.formSa.applyScopeId,
            boIds: viewEntity.formSa?.boIds,
            custTypeId: viewEntity.formSa.custTypeId,
            custSysCode: viewEntity.formSa.custSysCode,
            soldToRealCodes: viewEntity.formSa.soldToRealCodes,
            shipToCodes: viewEntity.formSa.shipToCodes,
          });
        }

        if (viewEntity.formSa?.formSaId) {
          const response = await FormSaDetail.getsEx(viewEntity.formSa?.formSaId)
          let relationFormExported = false;
          if (viewEntity.relatedFormId) {
            const relatedFormResp = await FormApi.get(viewEntity.relatedFormId);
            if (relatedFormResp.data.exportStatus == Enum.ExportStatus.EXPORT_SUCCESS) {
              relationFormExported = true;
            }
          }
          const newDataSource: any[] = [];
          response.data?.forEach((item) => {
            if (viewEntity.relationType == Enum.FormRelationType.REJECTED || viewEntity.relationType == Enum.FormRelationType.MODIFIED) {
              newDataSource.push(Object.assign({}, item, {formSaDetailIdOriginal: relationFormExported ? item.formSaDetailId : item.formSaDetailIdOriginal}));
            } else {
              newDataSource.push(Object.assign({}, item, {formSaDetailIdOriginal: null}));
            }
          });
          setDataSource(DataStore.init(newDataSource));
        }
        if (viewEntity.revenueType != Enum.RevenueType.BUY_GIVE) {
          const bohqResp = await BOHQ.getForPd();
          formRef.current?.setFieldsValue({bohqId: bohqResp?.data?.bohqId});
        }
        if (viewEntity.formSa?.boIds) {
          const detailResp = await BO.search({
            boIds: viewEntity.formSa?.boIds,
            isActive: true,
          });
          formRef.current?.setFieldsValue({boIds: detailResp.data?.map((item) => item.boId)});
        }
        if (viewEntity.formSa?.shipToCodes) {
          const detailResp = await ShipTo.search({
            shipToCodes: viewEntity.formSa?.shipToCodes,
            isActive: true,
          });
          formRef.current?.setFieldsValue({
            shipToCodes: {
              codes: viewEntity.formSa?.shipToCodes,
              options: detailResp.data.map((item) => ({
                value: item.shipToCode,
                label: item.codeOrName,
              })),
            },
          });
        }
        if (viewEntity.formSa?.soldToRealCodes) {
          const detailResp = await SoldToReal.search({
            soldToRealCodes: viewEntity.formSa?.soldToRealCodes,
            isActive: true,
          });
          formRef.current?.setFieldsValue({
            soldToRealCodes: {
              codes: viewEntity.formSa?.soldToRealCodes,
              options: detailResp.data.map((item) => ({
                value: item.soldToRealCode,
                label: item.codeOrName,
              })),
            },
          });
        }
        if (viewEntity.formSa?.soldToRealCodesExclude) {
          const detailResp = await SoldToReal.search({
            soldToRealCodes: viewEntity.formSa?.soldToRealCodesExclude,
            isActive: true,
          });
          formRef.current?.setFieldsValue({
            soldToRealCodesExclude: {
              codes: viewEntity.formSa?.soldToRealCodes,
              options: detailResp.data.map((item) => ({
                value: item.soldToRealCode,
                label: item.codeOrName,
              })),
            },
          });
        }
        if (viewEntity.revenueType) {
          const revenueTypeResp = await RevenueType.get(viewEntity.revenueType);
          formRef.current?.setFieldsValue({revenueTypeName: revenueTypeResp.data.revenueTypeName});
        }
        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && entity) {
      let title = '活动申请';
      if (entity.formSa) {
        title += '【编辑】';
      }
      props.setPageTitle(title);
    }
  }, [props.setPageTitle, entity]);

  return (
    <>
      <SAWarningInfoApp {...warningSettings} />
      <ProForm
        formRef={formRef}
        initialValues={{isOld: false}}
        submitter={{
          render: () => {
            return (
              <ApplyFooter
                viewEntity={entity}
                setLoading={props.setLoading}
                submitBackUrl="/sales/sa/myactivity"
                doSubmit={async (type: number, forceSubmit: boolean) => {
                  if (!entity || !dataSource) {
                    return;
                  }
                  const remark = await editorRef?.current?.getHtml?.();
                  const values = await formRef.current?.validateFieldsReturnFormatValue?.();
                  if (values && values.errorFields && values.errorFields.length) {
                    return;
                  }
                  const statusId =
                    type === ApplyFooterType.Draft
                      ? Enum.FormStatus.DRAFT
                      : Enum.FormStatus.AWAITING;
                  values.remark = remark;
                  const req = getPageResult(values, entity, statusId, dataSource);
                  if (req.code !== ResponseStatus.SUCCESS) {
                    message.error(req.message);
                    return;
                  }
                  props?.setLoading?.(true);
                  if (type === ApplyFooterType.Validate) {
                    return await FormSa.Buttons.validate(req.data);
                  } else {
                    return await FormSa.Buttons.apply({
                      forceSubmit: forceSubmit,
                      ...req.data,
                    });
                  }
                }}
                del={async () => {
                  if (!entity || !entity.formId) {
                    return;
                  }
                  return await FormSa.Buttons.del(entity.formId);
                }}
                showWarning={(result: WdiModalAppResult, submitCallback: any) => {
                  setWarningSettings({
                    visible: true,
                    title: '警示信息',
                    wdiPanelList: result.data,
                    onClose: () => {
                      setWarningSettings({visible: false});
                    },
                    onSave: async () => {
                      return await submitCallback();
                    },
                  });
                }}
              />
            );
          },
        }}
      >
        <WdiPanel title={<WdiCardTitle title="活动信息"/>}>
          <Row gutter={[16, 0]}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="activityName"
                label="活动名称"
                rules={[{required: true, message: '活动名称不能为空!'}]}
              >
                <Input/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="activityDate"
                label="活动期间"
                rules={[{required: true, message: '活动期间不能为空!'}]}
              >
                <RangePicker
                  onChange={(values) => {
                    if (values && values.length === 2) {
                      formRef.current?.setFieldsValue({supplyDate: values});
                      setFormSaDate({
                        activityStartDate: moment(values[0]).format('YYYY-MM-DD'),
                        activityEndDate: moment(values[1]).format('YYYY-MM-DD'),
                        supplyStartDate: moment(values[0]).format('YYYY-MM-DD'),
                        supplyEndDate: moment(values[1]).format('YYYY-MM-DD'),
                      });
                    } else {
                      setFormSaDate({});
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="supplyDate"
                label="供货期间"
                rules={[{required: true, message: '供货期间不能为空!'}]}
              >
                <RangePicker
                  onChange={(values) => {
                    if (values && values.length === 2) {
                      setFormSaDate(
                        Object.assign({}, formSaDate, {
                          supplyStartDate: moment(values[0]).format('YYYY-MM-DD'),
                          supplyEndDate: moment(values[1]).format('YYYY-MM-DD'),
                        }),
                      );
                    } else {
                      setFormSaDate(
                        Object.assign({}, formSaDate, {supplyStartDate: '', supplyEndDate: ''}),
                      );
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="bohqId"
                label="预算归属"
                rules={[{required: true, message: '预算归属不能为空!'}]}
              >
                <WdiSelect
                  {...{
                    placeholder: '请选择预算归属',
                    disabled: entity?.revenueType != Enum.RevenueType.BUY_GIVE,
                    dataLoading: async (params) => {
                      if (entity?.revenueType != Enum.RevenueType.BUY_GIVE) {
                        const bohqResp = await BOHQ.getForPd();
                        return [{value: bohqResp.data.bohqId, label: bohqResp.data.bohqName}];
                      } else {
                        const response = await BOHQ.search(Object.assign({
                            isActive: true,
                            withPermissions: true,
                          }, params,),
                        );
                        return response.data.map((item) => ({value: item.bohqId, label: item.bohqName}));
                      }
                    },
                    reloadKey: entity?.revenueType,
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="applyScopeId"
                label="适用范围类型"
                rules={[{required: true, message: '适用范围类型不能为空!'}]}
              >
                <WdiSelect
                  {...{
                    placeholder: '请选择适用范围类型',
                    showSearch: true,
                    dataLoading: async (params) => {
                      const response = await DataOptions.getApplyScopes();
                      return response.data.map((item) => {
                        return {value: item.value, label: item.name};
                      });
                    },
                    onChange: (value: any) => {
                      handleApplyScopeChange(value);
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="boIds"
                label="使用部门"
                rules={[{required: true, message: '使用部门不能为空!'}]}
              >
                <WdiTreeSelect
                  {...{
                    multiple: true,
                    placeholder: '请选择使用部门',
                    defaultExpandKeys: prevField?.boIds ? prevField?.boIds.split(',') : [],
                    dataLoading: async () => {
                      const response = await BO.search({
                        boLevels: [Enum.BOLevel.BO1, Enum.BOLevel.BO2, Enum.BOLevel.BO3].join(),
                        isActive: true,
                        isForPd: true,
                        excludeDefault: true,
                        withPermissions: prevField.applyScopeId != Enum.AdoptScope.CrossReginCustom,
                      });
                      const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
                        {
                          textField: 'boName',
                          valueField: 'boId',
                          id: 'boId',
                          pId: 'parentId',
                          level: 'boLevel',
                        },
                        response.data,
                      );
                      return {treeData, dict};
                    },
                    onExSelect: (value: any, node: any, dict: any) => {
                      const values = formRef.current?.getFieldValue('boIds');
                      let rtv = FieldProps.TreeSelect.labeledValuesMeger(dict, values, {value});
                      //按客户时控制使用部门不能跨1级
                      if (prevField.applyScopeId == Enum.AdoptScope.Custom) {
                        const noCurrent = rtv.filter((v: any) => v.value != value);
                        rtv = FieldProps.TreeSelect.topLevelMutexMeger(dict, noCurrent, {value}, Enum.BOLevel.BO1);
                      }
                      formRef.current?.setFieldsValue({
                        boIds: rtv,
                      });
                      handleBoIdsChange(rtv);
                    },
                    onDeselect: (value: any, node: any) => {
                      const values = formRef.current?.getFieldValue('boIds');
                      handleBoIdsChange(values);
                    },
                    reloadKey: prevField.applyScopeId,
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="custTypeId"
                label="客户分类"
                rules={[{required: true, message: '客户分类不能为空!'}]}
              >
                <WdiTreeSelect
                  {...{
                    placeholder: '请选择客户分类',
                    showSearch: true,
                    defaultExpandKeys: prevField?.custTypeId ? [String(prevField?.custTypeId)] : [],
                    reloadKey: prevField.applyScopeId,
                    dataLoading: async () => {
                      const response = await CustType.search({
                        withPermissions: prevField.applyScopeId != Enum.AdoptScope.CrossReginCustom,
                        isActive: true,
                        custTypeLevels: [
                          Enum.CustTypeLevel.LEVEL1,
                          Enum.CustTypeLevel.LEVEL2,
                          Enum.CustTypeLevel.LEVEL3,
                        ].join(),
                      });
                      let custTypes = response.data;

                      const L3_CODES = ['E_B', 'N_L', 'S_T'];
                      const L2_CODES = ['C_C'];
                      const L3_VALID_IDS = custTypes.filter(item =>
                        item?.custTypeCode && L3_CODES.includes((item?.custTypeCode))
                      ).map(item => (item.custTypeId));
                      const L2_VALID_IDS = custTypes.filter(item =>
                        item?.custTypeCode && L2_CODES.includes((item?.custTypeCode))
                      ).map(item => (item.custTypeId));
                      custTypes = custTypes.map((item: any) => {
                        if (L3_VALID_IDS.includes(item.custTypeId) || L3_VALID_IDS.includes(item.parentId)) {
                          item.disabled = true;
                        }
                        if (L2_VALID_IDS.includes(item.custTypeId)) {
                          item.disabled = true;
                        }
                        return item;
                      });

                      const {treeData, dict} = FieldProps.TreeSelect.formatTreeData(
                        {
                          textField: 'custTypeName',
                          valueField: 'custTypeId',
                          id: 'custTypeId',
                          pId: 'parentId',
                          level: 'custTypeLevel',
                        },
                        custTypes,
                        (node: any, item: any) => {
                          if (item.disabled) {
                            node.code = item.custTypeCode;
                            node.disableCheckbox = item.disabled;
                            node.selectable = !item.disabled;
                          }
                          return node;
                        }
                      );
                      return {treeData, dict};
                    },
                    onExSelect: (value: any, node: any, dict: any) => {
                      formRef.current?.resetFields([
                        'custSysCode',
                        'soldToRealCodes',
                        'shipToCodes',
                        'soldToRealCodesExclude',
                      ]);
                      handleFieldChange({
                        custTypeId: WdiSelectFormat.getvalue(value),
                        custSysCode: undefined,
                        soldToRealCodes: undefined,
                        shipToCodes: undefined,
                        soldToRealCodesExclude: undefined,
                      });
                      if (node.level >= Enum.CustTypeLevel.LEVEL2 && (node.code == 'S_TNA' || dict[node.pId].code == 'S_TNA')) {
                        setCustSysRequired(true);
                      } else {
                        setCustSysRequired(false);
                      }
                    },
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item name="custSysCode" label="客户组"
                         rules={[{required: custSysRequired, message: '客户组不能为空!'}]}>
                <WdiSelect
                  {...{
                    placeholder: '请选择客户组',
                    showSearch: true,
                    remote: true,
                    onChange: (value: any) => {
                      formRef.current?.resetFields(['soldToRealCodes', 'shipToCodes', 'soldToRealCodesExclude',]);
                      handleFieldChange({
                        custSysCode: WdiSelectFormat.getvalue(value),
                        soldToRealCodes: undefined,
                        shipToCodes: undefined,
                        soldToRealCodesExclude: undefined,
                      });
                    },
                    dataLoading: async (params) => {
                      const response = await CustSys.ajaxSearch(
                        Object.assign(
                          {
                            withPermissions: true,
                            isActive: true,
                            custTypeId: prevField?.custTypeId,
                            boIds: prevField?.boIds,
                          },
                          params,
                        ),
                      );
                      return response.data.map((item) => {
                        return {value: item.custSysCode, label: item.codeOrName};
                      });
                    },
                    reloadKey: `${prevField?.custTypeId}${prevField?.boIds}`,
                  }}
                />
              </Form.Item>
            </Col>
            {(prevField?.applyScopeId == Enum.AdoptScope.Custom ||
              prevField?.applyScopeId == Enum.AdoptScope.CrossReginCustom) && (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  name="soldToRealCodes"
                  label="ERP客户"
                  rules={[{required: true, message: 'ERP客户不能为空!'}]}
                >
                  <SoldToRealSelectApp
                    data={prevField}
                    onChange={(value) => {
                      if (prevField.soldToRealCodes != value?.codes) {
                        formRef.current?.resetFields(['shipToCodes']);
                        handleFieldChange({
                          soldToRealCodes: value?.codes,
                          shipToCodes: undefined,
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {(prevField?.applyScopeId == Enum.AdoptScope.Custom ||
              prevField?.applyScopeId == Enum.AdoptScope.CrossReginCustom) && (
              <Col className="gutter-row" span={4}>
                <Form.Item name="shipToCodes" label="收货地">
                  <ShipToSelect
                    data={prevField}
                    onChange={(value) => {
                      if (prevField.shipToCodes != value?.codes) {
                        handleFieldChange({
                          shipToCodes: value?.codes,
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            {(prevField?.applyScopeId == Enum.AdoptScope.CustomType) && (
              <Col className="gutter-row" span={4}>
                <Form.Item
                  name="soldToRealCodesExclude"
                  label="排除ERP客户"
                >
                  <SoldToRealSelectApp
                    data={prevField}
                    onChange={(value) => {
                      if (prevField.soldToRealCodesExclude != value?.codes) {
                        handleFieldChange({
                          soldToRealCodesExclude: value?.codes,
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col className="gutter-row" span={4}>
              <Form.Item name="isOld" valuePropName="checked" label="是否积压品">
                <Checkbox/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                name="revenueTypeName"
                label="核算类型"
                rules={[{required: true, message: '核算类型不能为空!'}]}
              >
                <WdiInput readOnly/>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[16, 24]}>
            <Col className="gutter-row" span={16}>
              <Form.Item
                name="remark"
                label="方案简述"
                rules={[{required: true, message: '方案简述不能为空!'}]}
              >
                <WdiEditor setRef={setEditorRef}/>
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={8}>
              <Form.Item
                name="fileInfo"
                label="附件"
                valuePropName="fileList"
                getValueFromEvent={(e: any) => {
                  return e.fileList || [];
                }}
              >
                <WdiFileUpload leftTopInfo="请选择文件并上传(单个文件不能超500M)"/>
              </Form.Item>
            </Col>
          </Row>
        </WdiPanel>
        <FormSaDetailsForApplyPanel
          data={{
            formSa: {
              pageTypeId: entity?.pageTypeId,
              formTypeId: entity?.formTypeId,
              ...prevField,
              ...formSaDate,
              revenueType: entity?.revenueType,
            },
          }}
          dataSource={dataSource}
          onChange={onDetailsChange}
        />
      </ProForm>
    </>
  );
};
export default PriceDiscountApplyApp;
